import { cn } from '@/lib/utils';
import type { LucideIcon } from '@/components/ui/icons/Icons';

type InfoBannerVariant = 'blue' | 'emerald' | 'red' | 'yellow' | 'orange';

const variantStyles: Record<
  InfoBannerVariant,
  { container: string; iconBg: string; icon: string; title: string; description: string }
> = {
  blue: {
    container: 'border-blue-200 bg-blue-50',
    iconBg: 'bg-blue-100',
    icon: 'text-blue-600',
    title: 'text-blue-900',
    description: 'text-blue-700',
  },
  emerald: {
    container: 'border-emerald-200 bg-emerald-50',
    iconBg: 'bg-emerald-100',
    icon: 'text-emerald-600',
    title: 'text-emerald-900',
    description: 'text-emerald-700',
  },
  red: {
    container: 'border-red-200 bg-red-50',
    iconBg: 'bg-red-100',
    icon: 'text-red-600',
    title: 'text-red-900',
    description: 'text-red-700',
  },
  yellow: {
    container: 'border-yellow-200 bg-yellow-50',
    iconBg: 'bg-yellow-100',
    icon: 'text-yellow-600',
    title: 'text-yellow-900',
    description: 'text-yellow-700',
  },
  orange: {
    container: 'border-orange-200 bg-orange-50',
    iconBg: 'bg-orange-100',
    icon: 'text-orange-600',
    title: 'text-orange-900',
    description: 'text-orange-700',
  },
};

interface InfoBannerProps {
  variant?: InfoBannerVariant;
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
}

export function InfoBanner({
  variant = 'blue',
  icon: Icon,
  title,
  description,
  className,
}: InfoBannerProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn('rounded-lg border p-4 flex items-center gap-3', styles.container, className)}
    >
      <div
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full',
          styles.iconBg
        )}
      >
        <Icon className={cn('size-5', styles.icon)} />
      </div>
      <div className="min-w-0">
        <h3 className={cn('text-sm font-semibold', styles.title)}>{title}</h3>
        {description && <p className={cn('text-sm mt-0.5', styles.description)}>{description}</p>}
      </div>
    </div>
  );
}
